import React, { useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi } from "ag-grid-community";
import { toast } from "react-toastify";
import {
  useGetChangeRequests,
  useApproveChangeRequest,
  useRejectChangeRequest,
} from "../../queries";
import { useEmployees } from "../../queries/employees";
import { Box, HStack, VStack } from "../../components/utils";
import BCHeader from "../../shared/BCHeader";
import { formatDisplayDate } from "../../utils/date";
import { CTADropdown } from "../../components/shared/Dropdown";
import StatusLabel from "../esopOverview/StatusLabel";
import ReviewApproveModal from "./ReviewApproveModal";

function PendingRequestsApproval() {
  const { data: changeRequests, isLoading } = useGetChangeRequests();
  const pendingChangeRequestsCount = (changeRequests ?? []).filter(
    (req) => req.status === "PENDING" || req.status === "REQUESTED"
  ).length;
  const { mutate: approveRequest, isLoading: isApproving } =
    useApproveChangeRequest();
  const { mutate: rejectRequest, isLoading: isRejecting } =
    useRejectChangeRequest();
  const gridApi = useRef<GridApi | null>(null);
  const { data: employees } = useEmployees();

  const [reviewRequest, setReviewRequest] = useState<any | null>(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);

  const onGridReady = (params: any) => {
    gridApi.current = params.api;
  };

  const openReviewModal = (request: any) => {
    setReviewRequest(request);
    setReviewModalOpen(true);
  };

  const closeReviewModal = () => {
    setReviewModalOpen(false);
    setReviewRequest(null);
  };

  const handleApprove = (changeRequestId: string) => {
    approveRequest(changeRequestId, {
      onSuccess: () => {
        closeReviewModal();
        toast("Request approved successfully", {
          autoClose: 2000,
          type: "success",
        });
      },
      onError: () => {
        toast("Error approving request", { autoClose: 2000, type: "error" });
      },
    });
  };

  const handleReject = (changeRequestId: string) => {
    rejectRequest(changeRequestId, {
      onSuccess: () => {
        closeReviewModal();
        toast("Request rejected successfully", {
          autoClose: 2000,
          type: "success",
        });
      },
      onError: () => {
        toast("Error rejecting request", { autoClose: 2000, type: "error" });
      },
    });
  };

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass:
        "multiline text-xs font-medium text-gray-dark leading-5 text-left",
      menuTabs: ["filterMenuTab"],
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
    }),
    []
  );

  const handleAction = (data: any, action: { name: string }) => {
    if (action.name === "Review and Approve") {
      openReviewModal(data);
    }
  };

  const renderField = (label: string, value: string | undefined) =>
    value ? (
      <HStack className="gap-1 text-xs">
        <span className="text-gray-500">{label}:</span>
        <span className="font-medium text-gray-dark">{value}</span>
      </HStack>
    ) : null;

  const formatDataByRequestType = (params: any) => {
    const data = params?.data?.data;
    if (!data) return <span>-</span>;
    const requestType = (params.data.requestType ?? "").toLowerCase();

    if (requestType === "exercise") {
      return (
        <VStack className="gap-0.5 py-1">
          {renderField(
            "Exercise Date",
            data.exerciseDate
              ? formatDisplayDate(new Date(data.exerciseDate))
              : undefined
          )}
        </VStack>
      );
    }

    if (requestType === "resignation" || requestType === "separation") {
      const resolvedHrId =
        data.hrId ??
        (employees ?? []).find((e) => e.id === data.employeeId)
          ?.employeeIdentificationString ??
        data.employeeId ??
        "?";
      return (
        <VStack className="gap-0.5 py-1">
          {renderField("HR ID", resolvedHrId)}
          {renderField(
            "Resignation Date",
            data.resignationDate
              ? formatDisplayDate(new Date(data.resignationDate))
              : undefined
          )}
          {renderField(
            "Last Day",
            data.lastDay ? formatDisplayDate(new Date(data.lastDay)) : undefined
          )}
        </VStack>
      );
    }

    if (requestType === "internal_transfer") {
      const oldEmp = (employees ?? []).find((e) => e.id === data.oldEmployeeId);
      const newEmp = data.newEmployeeId
        ? (employees ?? []).find((e) => e.id === data.newEmployeeId)
        : undefined;
      const resolvedOldHrId =
        data.oldHrId ??
        oldEmp?.employeeIdentificationString ??
        data.oldEmployeeId ??
        "?";
      const resolvedNewHrId =
        data.newHrId ??
        newEmp?.employeeIdentificationString ??
        data.newEmployeeId;
      const resolvedOldJoiningDate = data.oldJoiningDate ?? oldEmp?.dateOfJoin;
      const resolvedNewJoiningDate =
        data.newEmployeeDetailsToCreate?.dateOfJoining ?? newEmp?.dateOfJoin;
      return (
        <VStack className="gap-0.5 py-2">
          {renderField("HR ID", resolvedOldHrId)}
          {renderField(
            "Old Joining Date",
            resolvedOldJoiningDate
              ? formatDisplayDate(new Date(resolvedOldJoiningDate))
              : undefined
          )}
          {renderField(
            "New Joining Date",
            resolvedNewJoiningDate
              ? formatDisplayDate(new Date(resolvedNewJoiningDate))
              : undefined
          )}
        </VStack>
      );
    }

    return <span>{data.subject ?? "-"}</span>;
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "REQUEST TYPE",
        field: "requestType",
        minWidth: 160,
        valueFormatter: (params) => params.value.replace(/_/g, " ") ?? "-",
      },
      {
        headerName: "RAISED BY",
        field: "createdBy",
        minWidth: 180,
        valueFormatter: (params) => params.value.replace(/_/g, " ") ?? "-",
      },
      {
        headerName: "REQUEST DETAILS",
        field: "data",
        minWidth: 240,
        filter: "agTextColumnFilter",
        sortable: false,
        filterValueGetter: (params: any) => {
          const data = params?.data?.data;
          if (!data) return "";
          const requestType = (params.data.requestType ?? "").toLowerCase();

          if (requestType === "exercise") {
            return data.exerciseDate
              ? formatDisplayDate(new Date(data.exerciseDate))
              : "";
          }
          if (requestType === "resignation" || requestType === "separation") {
            return [
              data.hrId,
              data.resignationDate
                ? formatDisplayDate(new Date(data.resignationDate))
                : "",
              data.lastDay ? formatDisplayDate(new Date(data.lastDay)) : "",
            ]
              .filter(Boolean)
              .join(" ");
          }
          if (requestType === "internal_transfer") {
            const oldEmp = (employees ?? []).find(
              (e) => e.id === data.oldEmployeeId
            );
            const newEmp = data.newEmployeeId
              ? (employees ?? []).find((e) => e.id === data.newEmployeeId)
              : undefined;
            const oldJoiningDate = data.oldJoiningDate ?? oldEmp?.dateOfJoin;
            const newJoiningDate = data.newJoiningDate ?? newEmp?.dateOfJoin;
            return [
              data.oldHrId ??
                oldEmp?.employeeIdentificationString ??
                data.oldEmployeeId,
              oldEmp?.employeeName,
              data.newHrId ??
                newEmp?.employeeIdentificationString ??
                data.newEmployeeId,
              newEmp?.employeeName,
              oldJoiningDate
                ? formatDisplayDate(new Date(oldJoiningDate))
                : undefined,
              newJoiningDate
                ? formatDisplayDate(new Date(newJoiningDate))
                : undefined,
            ]
              .filter(Boolean)
              .join(" ");
          }
          return data.subject ?? "";
        },
        cellRenderer: (params: any) => formatDataByRequestType(params),
      },
      {
        headerName: "STATUS",
        field: "status",
        minWidth: 130,
        cellRenderer: (params: any) =>
          params.value ? (
            <StatusLabel state={getRequestStatus(params.value)} />
          ) : (
            "-"
          ),
        comparator: (valueA: string, valueB: string) => {
          const statusOrder: Record<string, number> = {
            PENDING: 0,
            REQUESTED: 0,
            RESIGNED: 1,
            RESIGNATION: 1,
            DRAFT: 2,
            APPROVED: 3,
          };
          const orderA = statusOrder[valueA?.toUpperCase()] ?? 2;
          const orderB = statusOrder[valueB?.toUpperCase()] ?? 2;
          return orderA - orderB;
        },
        sort: "asc",
      },
      {
        headerName: "RAISED ON",
        field: "createdAt",
        minWidth: 180,
        valueFormatter: (params) =>
          params.value ? formatDisplayDate(new Date(params.value)) : "-",
      },
      {
        headerName: "LAST UPDATED",
        field: "updatedAt",
        minWidth: 180,
        valueFormatter: (params) =>
          params.value ? formatDisplayDate(new Date(params.value)) : "-",
      },
      {
        headerName: "",
        field: "actions",
        pinned: "right",
        hide: false,
        width: 80,
        maxWidth: 80,
        filter: false,
        colId: "action-column",
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        resizable: false,
        sortable: false,
        menuTabs: [],
        cellRendererParams: ({ value }: { value: { props: any } }) =>
          value ? value.props : null,
        cellRenderer: CTADropdown,
      },
    ],
    [employees]
  );

  const rowData = useMemo(
    () =>
      (changeRequests ?? []).map((request: any) => ({
        ...request,
        actions: (
          <CTADropdown
            dropdownClassName="xl:right-10 lg:right-8 md:right-2"
            actions={[{ name: "Review and Approve" }]}
            onAction={(action) => handleAction(request, action)}
          />
        ),
      })),
    [changeRequests]
  );

  const noDataImageUrl =
    "https://equion-dev.s3.us-west-2.amazonaws.com/assets/emptyTable.png";

  return (
    <VStack className="gap-4">
      <ReviewApproveModal
        open={reviewModalOpen}
        request={reviewRequest}
        isApproving={isApproving}
        isRejecting={isRejecting}
        onApprove={handleApprove}
        onReject={handleReject}
        onClose={closeReviewModal}
      />
      <div className="flex items-baseline justify-between">
        <BCHeader
          className="items-baseline"
          bcTitle="Employee Data Sync"
          bcSubTitle="Pending Requests Approval"
        />
      </div>
      {!isLoading && rowData.length === 0 && (
        <Box className="text-center justify-center align-middle">
          <img src={noDataImageUrl} alt="" className="h-44 my-5 w-56 mx-auto" />
          <p className="text-xxs pt-4">No pending requests found</p>
        </Box>
      )}
      {rowData.length > 0 && (
        <Box className="text-center justify-center align-middle">
          <HStack className="items-center justify-between gap-4 bg-white p-4">
            <VStack className="items-start">
              <p className="text-lg font-medium text-headerColor whitespace-nowrap">
                Pending Requests Approval
              </p>
              <p className="text-xs text-gray-500">
                Review and approve requests raised from the Darwin Box Sync
                integration{" "}
                {pendingChangeRequestsCount > 0
                  ? `(${pendingChangeRequestsCount ?? 0} pending)`
                  : ""}
              </p>
            </VStack>
          </HStack>
          <HStack className="justify-between w-full">
            <Box
              style={{
                height: `${
                  (rowData.length >= 10 ? 10 : rowData.length + 3) * 60
                }px`,
              }}
              className="w-full h-full max-h-full overflow-x-auto bg-black ag-theme-material"
            >
              <AgGridReact
                alwaysShowHorizontalScroll
                rowClass="border-t border-dashed cursor-pointer hover:bg-slate-50"
                alwaysMultiSort
                suppressRowTransform={true}
                onGridReady={onGridReady}
                animateRows={true}
                defaultColDef={defaultColDef}
                rowData={rowData}
                suppressCopyRowsToClipboard={true}
                suppressCopySingleCellRanges={true}
                suppressCellFocus={true}
                suppressMenuHide={true}
                columnDefs={columnDefs}
                pagination={true}
                overlayNoRowsTemplate={
                  '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Rows To Show</span>'
                }
              />
            </Box>
          </HStack>
        </Box>
      )}
    </VStack>
  );
}

export const getRequestStatus = (state: string) => {
  if (state === "APPROVED") return "COMPLETED";
  return state;
};

export default PendingRequestsApproval;
